import Link from 'next/link';
import { ReactNode } from 'react';

import { Heading, Paragraph } from '@/components/dom/text-elements';
import { TeamAvatar } from '@/components/global/avatar';
import Icon from '@/components/global/icon';
import { GraphProps } from '@/components/graph/index-graph';
import IndexInfoList from '@/components/index/index-info-list';
import { IndexSparkRow } from '@/components/products/index-spark-row';
import { ParseGqlOptionalUtcDateTime } from '@/helpers/dates';
import cn from '@/lib/cn';
import { SpotlightCardType } from '@/queries/spotlight-card';

type SpotlightCreatorDataType = {
    title: string | ReactNode;
    text: string | ReactNode;
    name: string;
    imageUrl?: string;
    teamPageUrl?: string;
    symbol: string;
    twitterHandle?: string;
};

// TODO: migrate this into API if possible? this has ReactNode elements though...
const SPOTLIGHT_CREATOR_DATA_BY_SYMBOL: { [key: string]: SpotlightCreatorDataType } = [
    {
        imageUrl: '/assets/product/doug.png',
        name: 'Intelligent Indices',
        symbol: 'AISPS',
        teamPageUrl: '/team/01h4s6jxjhpm9g83jg9b5pkjvm',
        text: (
            <>
                Intelligent Indices believes the future of passive investing is intelligent and challenges traditional,
                unintelligent index products — those that don&apos;t leverage the power of AI. Using leading AI
                platforms ChatGPT, Bard, and Claude, they have developed many AI-powered indices, including the flagship
                $AISPS: a custom AI-weighted index of top expected large cap performers inspired by the S&P 500.
            </>
        ),
        title: 'AI-Powered Indices',
    },
    {
        imageUrl: '/assets/product/howie.png',
        name: 'Howard Lindzon',
        symbol: 'DEGENCOM',
        teamPageUrl: '/team/01gpf5jetfk2f9m3p8a1ewsefk',
        text: (
            <>
                For the last year, renowned investor and serial entrepreneur Howard Lindzon has been digging into what
                he calls the &lsquo;degenerate economy&rsquo;, which is the next phase of investing, ownership,
                gambling, and living life with a wallet on/in your phone. The &lsquo;degenerate economy&rsquo; includes
                education, experiences, and activities. $DEGENCOM highlights the companies that have onboarded and
                connected hundreds of millions of young people into investing, trading, speculation, crypto, and
                gambling/betting.
            </>
        ),
        title: 'Living life on your phone',
        twitterHandle: 'howardlindzon',
    },
    {
        imageUrl: '/assets/product/grace.png',
        name: 'Grace Isford',
        symbol: 'LUXDEX',
        teamPageUrl: '/team/01fsmf95zsjsr686chvxfrktap',
        text: `To Grace Isford, partner at Lux Capital, the infrastructure being built to support Web3 is where long-term value creation lives. The $LUXDEX ignores the hype and focuses specifically on those companies and projects building the backbone of Web3. The $LUXDEX is the first index of its kind, combining public equities and crypto tokens in a financial index.`,
        title: 'Hype-free Web3',
        twitterHandle: 'graceisford',
    },
    {
        imageUrl: '/assets/product/lenny.png',
        name: 'Lenny Rachitsky',
        symbol: 'LENNY',
        teamPageUrl: '/team/01g0qcetnrg5b82bwca3evt8t8',
        text: 'How do you measure success in enterprise SaaS? According to Lenny Rachitsky, an expert on product led growth and the principal behind Lenny’s Newsletter and Lenny’s Podcast, the best companies and business models are those that grow by becoming more valuable to their customers over time. $LENNY represents the best-in-class SaaS companies as measured by Net Revenue Retention (NRR). ',
        title: 'Land and expand',
        twitterHandle: 'lennysan',
    },
].reduce((acc, creator) => {
    const { symbol } = creator;
    return { ...acc, [symbol]: creator };
}, {});

export const SPOTLIGHT_CREATOR_SYMBOLS = Object.keys(SPOTLIGHT_CREATOR_DATA_BY_SYMBOL).map((symbol) =>
    symbol.toUpperCase()
);

type SpotlightCardProps = {
    index: SpotlightCardType;
    hidePortrait?: boolean;
    className?: string;
    graphProps?: Partial<GraphProps>;
};

const SpotlightCard = ({ index, hidePortrait = false, className, graphProps }: SpotlightCardProps) => {
    const { organization, symbol } = index;
    const { title, text, name, teamPageUrl, imageUrl, twitterHandle } = SPOTLIGHT_CREATOR_DATA_BY_SYMBOL[symbol];
    const firstEffectiveAtDate = ParseGqlOptionalUtcDateTime(index.firstEffectiveAt);

    return (
        <div className={cn('flex flex-col lg:flex-row rounded-lg overflow-hidden shadow-md', className)}>
            <div className="flex bg-brand-black-alt w-full lg:w-1/2 flex-grow flex-shrink-0 p-6 gap-6 flex-col md:flex-row lg:flex-col xl:flex-row">
                {imageUrl && (
                    <div className="shrink-0">
                        {!hidePortrait && imageUrl && (
                            <img
                                alt={name}
                                src={imageUrl}
                                width={280}
                                className="mb-4"
                            />
                        )}

                        {twitterHandle && (
                            <Link
                                href={`https://x.com/${twitterHandle}`}
                                target="_blank"
                                className="text-white visited:text-white no-underline flex items-center mb-4 sm:mb-0"
                            >
                                <Icon
                                    type="xTwitter"
                                    className="mr-1 mt-1"
                                    color="white"
                                />
                                @{twitterHandle}
                            </Link>
                        )}
                    </div>
                )}

                <div>
                    <header className="mb-8">
                        <Heading
                            importance={4}
                            className="mb-6 text-brand-gray-shell tracking-wide"
                        >
                            Team Spotlight
                        </Heading>
                    </header>

                    <div>
                        <Heading
                            importance={1}
                            className="text-white mb-6"
                        >
                            {title}
                        </Heading>

                        <Paragraph className="text-brand-chartreuse mb-10 uppercase tracking-widest">
                            By {name}
                        </Paragraph>

                        <Paragraph className="text-brand-gray-shell">{text}</Paragraph>
                    </div>
                </div>
            </div>

            <div className="w-full lg:w-1/2 flex-grow flex-shrink-0 bg-white p-8">
                <header className="flex gap-6 mb-6">
                    <TeamAvatar
                        url={organization.avatarUrl}
                        name={organization.name}
                        className="w-[60px] h-[60px]"
                    />

                    <div className="flex flex-col gap-2">
                        <Paragraph className="text-brand-blue-nu font-brand-md tracking-wider mb-0">TEAM</Paragraph>

                        <Heading
                            importance={4}
                            className="text-brand-black-alt"
                        >
                            {name}
                        </Heading>
                    </div>
                </header>

                <div className="border border-brand-gray-med rounded-md overflow-hidden p-4 mb-4">
                    <Paragraph className="text-brand-blue-nu font-brand-md text-sm mb-2">Featured Index</Paragraph>

                    <IndexSparkRow
                        index={index}
                        className="text-sm flex-grow mb-3"
                        titleLinkClassName="mb-2"
                        timeRangeLabelText="3 Months"
                        graphProps={{ height: 106, ...graphProps }}
                    />

                    <IndexInfoList
                        symbol={symbol}
                        firstEffectiveAtDate={firstEffectiveAtDate}
                        weightType={index.activeVersion?.strategy?.__typename || ''}
                        numberOfHoldings={index.latestSnapshot?.components?.length || 0}
                    />
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Paragraph className="mb-0 font-brand-bold text-brand-black-med">Indices Created</Paragraph>
                        <span className="text-brand-gray-darker">({organization.publicIndexes.length})</span>
                    </div>

                    {teamPageUrl && (
                        <Link
                            href={teamPageUrl}
                            className="font-brand-bold no-underline visited:text-brand-blue-yves text-brand-blue-yves hover:text-brand-blue-nu"
                        >
                            See them all
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpotlightCard;
